import { map } from 'lodash/fp';
import Checkbox from '../../../../components/Checkbox';
import DatePicker from '../../../../components/DateTime/DatePicker';
import { FormLabel } from '../../../../components/Form/FormLabel';
import { Option, Select } from '../../../../components/Select';
import { getCurrentUser } from '../../../../services/CurrentUser';

import moment from 'moment';
import { Card } from '../../../../components/Cards';
import { euAllowExternalServices, grantSiImpersonationUntil, notifyReviewersEveryNHours } from '../constants';
import {
    getAllowExternalServices,
    getGrantSiImpersonation,
    getGrantSiImpersonationUntil,
    getNotifyReviewersEveryNHours,
    setAllowExternalServices,
    setGrantSiImpersonation,
    setGrantSiImpersonationUntil,
    setNotifyReviewersEveryNHours,
} from '../model';
import { ModelProps } from '../presenter';
import styles from '../style.m.less';
import SettingsRow from './SettingsRow';

const GrantImpersonationSettings = ({ model, setModel }: ModelProps) => {
    function formatTimestampToDateString(timestamp: number) {
        if (!timestamp) {
            return undefined;
        }
        return moment(timestamp).format('YYYY-MM-DD');
    }

    return (
        <SettingsRow label="Small Improvements staff access">
            <Checkbox
                label="Allow Small Improvements staff in"
                type="checkbox"
                id="grantSiImpersonation"
                checked={getGrantSiImpersonation(model)}
                onChange={e => setModel(setGrantSiImpersonation(e.target.checked, model))}
                hiddenLabel={true}
            />
            <FormLabel htmlFor="grantSiImpersonation">Grant SI staff access</FormLabel>
            <br />
            <FormLabel htmlFor="grantSiImpersonationUntil">Grant Access Until</FormLabel>
            <br />
            <span className={styles.pickerStart}>
                <DatePicker
                    date={formatTimestampToDateString(getGrantSiImpersonationUntil(model))}
                    id="grantSiImpersonationUntil"
                    onChange={(date: string | undefined) => setModel(setGrantSiImpersonationUntil(date, model))}
                    isClearable
                />
            </span>
            (blank means permanently)
            <br />
            <p className="caption">{grantSiImpersonationUntil}</p>
        </SettingsRow>
    );
};

const ReviewerNotificationSettings = ({ model, setModel }: ModelProps) => {
    const ReviewerNotificationProps = {
        selected: getNotifyReviewersEveryNHours(model),
        optionToText: (hour: number) => `${hour}`,
        onSelect: (hour: number) => setModel(setNotifyReviewersEveryNHours(hour, model)),
        className: styles.hourSelect,
    };

    return (
        <SettingsRow label="360° Feedback Reviewer Notification Interval">
            <div className={styles.flexAlignCenter}>
                <span className={styles.pickerStart}>
                    <Select {...ReviewerNotificationProps}>
                        {map(
                            hour => {
                                return (
                                    <Option key={hour} value={hour}>
                                        {hour}
                                    </Option>
                                );
                            },
                            [1, 2, 4, 6, 8, 12, 24]
                        )}
                    </Select>
                </span>
                Hours
            </div>
            <p className="caption">{notifyReviewersEveryNHours}</p>
        </SettingsRow>
    );
};

const ExternalServicesSettings = ({ model, setModel }: ModelProps) => (
    <SettingsRow label="Help us">
        <Checkbox
            label="Allow external services"
            type="checkbox"
            id="euAllowExternalServices"
            checked={getAllowExternalServices(model)}
            onChange={e => setModel(setAllowExternalServices(e.target.checked, model))}
            hiddenLabel={true}
        />
        <FormLabel htmlFor="euAllowExternalServices">Allow external services</FormLabel>

        <p className="caption">{euAllowExternalServices}</p>
    </SettingsRow>
);

const AdvancedSettings = ({ model, setModel }: ModelProps) => {
    const euServerOrLocal = getCurrentUser().company.server;

    return (
        <div>
            <h2>Advanced</h2>
            <Card>
                <GrantImpersonationSettings model={model} setModel={setModel} />
                <ReviewerNotificationSettings model={model} setModel={setModel} />
                {(euServerOrLocal === 'local' || euServerOrLocal === 'prod-eu') && (
                    <ExternalServicesSettings model={model} setModel={setModel} />
                )}
            </Card>
        </div>
    );
};

export default AdvancedSettings;
